import { TranslocoModule, provideTranslocoScope, translate } from '@jsverse/transloco';
import { Component, DestroyRef, OnDestroy, OnInit, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { HeaderService } from 'core';
import { LoadingSpinner } from 'design-system';
import { map, of, switchMap } from 'rxjs';
import { NewCustomerResult, NewCustomerSearchRequest } from '../../interfaces/new-customer-requests.model';
import { NewCustomerService } from '../../services/new-customer.service';
import { OrderProposalService } from '../../services/order-proposal.service';
import { digitsOnlyControl, isValidTaxCode, isValidVat, searchType, upperCaseControl } from '../../utils/new-customer.utils';
import { proposalNeedsCodeChoice } from '../../utils/order.utils';
import { CustomerBlockedDialog } from '../select-customer/customer-blocked-dialog/customer-blocked-dialog';
import { NewCustomerBank } from './new-customer-bank/new-customer-bank';
import { NewCustomerBanner } from './new-customer-banner/new-customer-banner';
import { NewCustomerCompanyDetail } from './new-customer-company-detail/new-customer-company-detail';
import { NewCustomerContact } from './new-customer-contact/new-customer-contact';
import { NewCustomerDeliveries } from './new-customer-deliveries/new-customer-deliveries';
import { NewCustomerDeliveryForm } from './new-customer-delivery-form/new-customer-delivery-form';
import { NewCustomerExitDialog } from './new-customer-exit-dialog/new-customer-exit-dialog';
import { NewCustomerGdpr } from './new-customer-gdpr/new-customer-gdpr';
import { NewCustomerHeader } from './new-customer-header/new-customer-header';
import { NewCustomerManual } from './new-customer-manual/new-customer-manual';
import { NewCustomerNote } from './new-customer-note/new-customer-note';
import { NewCustomerResults } from './new-customer-results/new-customer-results';
import { NewCustomerZones } from './new-customer-zones/new-customer-zones';

// Nuovo cliente per la proposta: si parte dalla ricerca nel Registro delle Imprese, gli altri step si aprono in sequenza
@Component({
  selector: 'ord-new-customer',
  imports: [
    LoadingSpinner,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    NewCustomerBank,
    NewCustomerBanner,
    NewCustomerCompanyDetail,
    NewCustomerContact,
    NewCustomerDeliveries,
    NewCustomerDeliveryForm,
    NewCustomerGdpr,
    NewCustomerHeader,
    NewCustomerManual,
    NewCustomerNote,
    NewCustomerResults,
    NewCustomerZones,
    ReactiveFormsModule,
    TranslocoModule,
  ],
  providers: [provideTranslocoScope('orders')],
  templateUrl: './new-customer.html',
  styleUrl: './new-customer.scss',
})
export class NewCustomer implements OnInit, OnDestroy {
  private readonly headerService = inject(HeaderService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly formBuilder = inject(FormBuilder);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly proposalService = inject(OrderProposalService);
  readonly flow = inject(NewCustomerService);

  // L'ultima ricerca non ha trovato nulla: si può creare il cliente a mano
  readonly noResults = signal(false);

  // La scheda con ragione sociale, codice fiscale e partita IVA sta sugli step dopo la scelta dell'azienda
  readonly showHeader = computed(() => !['search', 'results', 'detail', 'manual'].includes(this.flow.step()));

  readonly form = this.formBuilder.nonNullable.group({
    ragioneSociale: [''],
    codiceFiscale: [''],
    partitaIva: [''],
  });

  // Il flusso finito chiude la pagina: proposta sul nuovo cliente oppure cliente già in anagrafica
  private readonly finished = effect(() => {
    const result = this.flow.result();
    if (result) {
      this.finish(result);
    }
  });

  ngOnInit(): void {
    const idOrdine = this.route.snapshot.queryParamMap.get('id') ?? '';
    this.headerService.setTitle('orders.ui.nuovoCliente');
    this.headerService.setActions([{ get label() { return translate<string>('orders.ui.chiudi'); }, icon: 'fa-xmark', action: () => this.close() }]);

    this.flow.start(idOrdine);
    this.proposalService.ensure(idOrdine).subscribe();

    upperCaseControl(this.form.controls.codiceFiscale, this.destroyRef);
    digitsOnlyControl(this.form.controls.partitaIva, this.destroyRef);

    // Si cerca per un solo criterio: gli altri campi si spengono
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      const controls = Object.values(this.form.controls);
      const activeControl = controls.find(control => control.value.length > 0);

      for (const control of controls) {
        if (activeControl && control !== activeControl) {
          control.disable({ emitEvent: false });
        } else {
          control.enable({ emitEvent: false });
        }
      }
      this.noResults.set(false);
    });
  }

  ngOnDestroy(): void {
    this.headerService.removeTitle();
    this.headerService.removeActions();
  }

  get canSearch(): boolean {
    const request = this.request;
    switch (searchType(request)) {
      case 'partitaIva':
        return isValidVat(request.partitaIva ?? '');
      case 'codiceFiscale':
        return isValidTaxCode(request.codiceFiscale ?? '');
      default:
        return !!request.ragioneSociale;
    }
  }

  // L'errore compare solo da 11 caratteri, quando il codice potrebbe essere completo
  get taxCodeError(): boolean {
    const value = this.form.controls.codiceFiscale.value;
    return value.length >= 11 && !isValidTaxCode(value);
  }

  search(): void {
    if (!this.canSearch || this.flow.loading()) {
      return;
    }

    this.noResults.set(false);
    this.flow.search(this.request).subscribe({
      next: companies => {
        if (companies.items.length > 0) {
          this.flow.go('results');
        } else {
          this.noResults.set(true);
        }
      },
      error: (error: unknown) => {
        const message = this.flow.errorMessage(error);
        if (message) {
          this.snackBar.open(message, translate<string>('orders.ui.chiudi'), { duration: 5000 });
        }
      },
    });
  }

  createManually(): void {
    this.flow.go('manual');
  }

  // Con dati inseriti si chiede conferma prima di perderli
  close(): void {
    const hasInput = Object.values(this.form.getRawValue()).some(value => value.trim().length > 0);
    if (!hasInput && !this.flow.hasData()) {
      this.exit();
      return;
    }

    this.dialog
      .open<NewCustomerExitDialog, unknown, boolean>(NewCustomerExitDialog, { width: '400px' })
      .afterClosed()
      .subscribe(confirmed => {
        if (confirmed) {
          this.exit();
        }
      });
  }

  private get request(): NewCustomerSearchRequest {
    const value = this.form.getRawValue();
    return {
      ragioneSociale: value.ragioneSociale.trim() || undefined,
      codiceFiscale: value.codiceFiscale.trim() || undefined,
      partitaIva: value.partitaIva.trim() || undefined,
    };
  }

  private exit(): void {
    void this.router.navigate(['/new-order-proposal/select-customer'], {
      queryParams: { id: this.flow.idOrdine() },
    });
  }

  private finish(result: NewCustomerResult): void {
    if (result.proposalId) {
      void this.router.navigate(['/new-order-proposal'], { queryParams: { id: result.proposalId }, replaceUrl: true });
      return;
    }

    if (!result.idBusinessPartner) {
      return;
    }

    // Cliente già in anagrafica: si sceglie come dalla lista, coi codici se servono; «Annulla» torna alla lista col cliente di prima
    const previous = this.proposalService.proposal()!;
    this.flow.result.set(null);
    this.proposalService.setCustomer(result.idBusinessPartner).pipe(
      switchMap(proposal => (proposalNeedsCodeChoice(proposal) ? this.proposalService.chooseCustomerCodes() : of(proposal))),
      switchMap(proposal => (proposal ? of(proposal) : this.proposalService.restoreCustomer(previous).pipe(map(() => null)))),
    ).subscribe({
      next: proposal => {
        if (!proposal) {
          this.exit();
          return;
        }
        void this.router.navigate(['/new-order-proposal'], {
          queryParams: { id: this.proposalService.proposal()?.id },
          replaceUrl: true,
        });
      },
      error: (error: HttpErrorResponse) => {
        // 400: blocco attivo o nessun codice valido, col motivo del backend
        if (error.status === 400) {
          this.dialog.open(CustomerBlockedDialog, {
            width: '400px',
            data: { message: error.error?.detail ?? '' },
          });
        }
      },
    });
  }
}
